import { Injectable, OnInit, signal } from '@angular/core';

@Injectable({
  providedIn: 'root',
})
export class ConfigService implements OnInit {
  key = 'config';
  from = signal<Date>(new Date());
  to = signal<Date>(new Date());

  private toStartOfDay(date: Date): Date {
    const normalized = new Date(date);
    normalized.setHours(0, 0, 0, 0);
    return normalized;
  }

  private toEndOfDay(date: Date): Date {
    const normalized = new Date(date);
    normalized.setHours(23, 59, 59, 999);
    return normalized;
  }

  private toDateOnlyString(date: Date): string {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  }

  getApiDateRange = (): { from: string; to: string } => {
    return {
      from: this.toDateOnlyString(this.from()),
      to: this.toDateOnlyString(this.to()),
    };
  };

  getApiDateTimeRange = (): { from: string; to: string } => {
    return {
      from: this.formatDateTimeForApi(this.from()),
      to: this.formatDateTimeForApi(this.to(), true),
    };
  };

  formatDateTimeForApi = (date: Date, endOfDay = false): string => {
    const normalized = endOfDay
      ? this.toEndOfDay(date)
      : this.toStartOfDay(date);

    const year = normalized.getFullYear();
    const month = String(normalized.getMonth() + 1).padStart(2, '0');
    const day = String(normalized.getDate()).padStart(2, '0');
    const hours = String(normalized.getHours()).padStart(2, '0');
    const minutes = String(normalized.getMinutes()).padStart(2, '0');
    const seconds = String(normalized.getSeconds()).padStart(2, '0');
    const milliseconds = String(normalized.getMilliseconds()).padStart(3, '0');

    const offsetMinutes = normalized.getTimezoneOffset();
    const sign = offsetMinutes <= 0 ? '+' : '-';
    const absoluteOffsetMinutes = Math.abs(offsetMinutes);
    const offsetHours = String(Math.floor(absoluteOffsetMinutes / 60)).padStart(2, '0');
    const offsetRemainderMinutes = String(absoluteOffsetMinutes % 60).padStart(2, '0');

    return `${year}-${month}-${day}T${hours}:${minutes}:${seconds}.${milliseconds}${sign}${offsetHours}:${offsetRemainderMinutes}`;
  };

  ngOnInit() {
    const currentDate = new Date();
    // currentDate.setUTCHours(23, 59, 59, 999); // extending the range to the end of the current day

    const from = new Date(currentDate.getFullYear(), currentDate.getMonth(), 1);
    const to = currentDate;

    this.setDate({ from, to });
  }

  constructor() {
    this.loadConfig();
  }

  /**
   * @description initializes date range with default values (last year to today)
   */
  private initDateRange = () => {
    const currentDate = new Date();
    // currentDate.setUTCHours(23, 59, 59, 999);

    const from = new Date(currentDate.getFullYear(), currentDate.getMonth(), 1);
    const to = currentDate;

    this.setDate({ from, to });
  };

  /**
   * @description resets config date range to default values (first day of current month to today)
   */
  resetToDefaultDateRange = () => {
    this.initDateRange();
  };

  /**
   * @description loads config from localStorage and initializes from/to signals
   */
  loadConfig = () => {
    const stored = window.sessionStorage.getItem(this.key);
    if (stored) {
      try {
        const parsed = JSON.parse(stored);
        if (parsed?.from) this.from.set(new Date(parsed.from));
        if (parsed?.to) this.to.set(new Date(parsed.to));
        console.log(parsed);
      } catch {
        // if parsing fails, use default initialization
        this.initDateRange();
      }
    } else {
      // no config in localStorage, initialize with defaults
      this.initDateRange();
    }
  };

  /**
   * @description sets timeRange config
   * @param {Object} timeRange
   * @param {Date} timeRange.from Time range from date
   * @param {Date} timeRange.to Time range To date
   */
  saveConfig = () => {
    window.sessionStorage.setItem(
      this.key,
      JSON.stringify({ from: this.from(), to: this.to() }),
    );
  };

  /**
   * @description sets timeRange config
   * @param {Object} timeRange
   * @param {Date} timeRange.from Time range from date
   * @param {Date} timeRange.to Time range To date
   */
  setDate = ({ from, to }: { from: Date; to: Date }) => {
    this.from.set(from);
    this.to.set(to);

    this.saveConfig();
  };
}
